/**
 * R-78 / R-67 — audit / QA pipeline configuration.
 */
export const AUDIT_CONFIG = {
  /** When true, apply tier-1 precedence auto-corrections before persistence. Default off. */
  AUTOCORRECT_PRECEDENCE:
    (typeof process !== 'undefined' && process.env?.AUDIT_AUTOCORRECT_PRECEDENCE === 'true')
    || (typeof import.meta !== 'undefined' && import.meta.env?.VITE_AUDIT_AUTOCORRECT_PRECEDENCE === 'true'),
  /** When true, reconcile within-report numeric inconsistencies before persistence. Default off. */
  AUTOCORRECT_INTERNAL_NUMBERS:
    (typeof process !== 'undefined' && process.env?.AUDIT_AUTOCORRECT_INTERNAL_NUMBERS === 'true')
    || (typeof import.meta !== 'undefined' && import.meta.env?.VITE_AUDIT_AUTOCORRECT_INTERNAL_NUMBERS === 'true'),
  /** When true, HEAD-check reference URLs during R-66 citation integrity (5s timeout, 24h cache). */
  CHECK_URLS:
    (typeof process !== 'undefined' && process.env?.AUDIT_CHECK_URLS === 'true')
    || (typeof import.meta !== 'undefined' && import.meta.env?.VITE_AUDIT_CHECK_URLS === 'true'),
  /** When true, rerun narrative once when R-66 finds blocking citation violations. Default off in prod. */
  AUTO_REGENERATE_ON_BROKEN_CITES:
    (typeof process !== 'undefined' && process.env?.AUDIT_AUTO_REGENERATE_CITES === 'true')
    || (typeof import.meta !== 'undefined' && import.meta.env?.VITE_AUDIT_AUTO_REGENERATE_CITES === 'true'),
  /**
   * When true, R-66 downgrades citations lacking verifiable provenance to
   * `[unverified]` markers in the report body and surfaces a "N citations marked
   * for verification" note. DISABLED by default per product decision — reports
   * keep their original `[Ref N]` citations and show no in-report verification
   * banner (genuine block-severity citation errors still surface in the Audit tab).
   */
  APPLY_CITATION_PROVENANCE_DOWNGRADE:
    (typeof process !== 'undefined' && process.env?.AUDIT_CITATION_PROVENANCE_DOWNGRADE === 'true')
    || (typeof import.meta !== 'undefined' && import.meta.env?.VITE_CITATION_PROVENANCE_DOWNGRADE === 'true'),
  /** R-63 — run LLM judge for sections with keyword/embedding warn+ findings. Default off. */
  ENABLE_LLM_JUDGE:
    (typeof process !== 'undefined' && process.env?.AUDIT_ENABLE_LLM_JUDGE === 'true')
    || (typeof import.meta !== 'undefined' && import.meta.env?.VITE_AUDIT_ENABLE_LLM_JUDGE === 'true'),
  /** R-63 — cap LLM judge invocations per report generation run. */
  LLM_JUDGE_MAX_PER_RUN: 8,
  /**
   * R-68 — when true, prepend buried major constraints to Key Findings before
   * persistence. Default ON (QA 2026-06-10: the elevation gate was already
   * building the correct patch — e.g. four block-severity bullets on the Holly
   * Ridge test-fit — but never applying it with the flag off, so decisive
   * constraints stayed buried outside Key Findings). Set
   * AUDIT_AUTO_ELEVATE_CONSTRAINTS=false / VITE_AUDIT_AUTO_ELEVATE_CONSTRAINTS=false
   * to disable; admins can still revert per-report via the Audit tab
   * (revertConstraintElevation keeps the pre-patch original).
   */
  AUTO_ELEVATE_CONSTRAINTS: !(
    (typeof process !== 'undefined' && process.env?.AUDIT_AUTO_ELEVATE_CONSTRAINTS === 'false')
    || (typeof import.meta !== 'undefined' && import.meta.env?.VITE_AUDIT_AUTO_ELEVATE_CONSTRAINTS === 'false')
  ),
  /** R-69 — when true, apply semantic action deduplication before persistence. Default off. */
  AUTO_DEDUP_ACTIONS:
    (typeof process !== 'undefined' && process.env?.AUDIT_AUTO_DEDUP_ACTIONS === 'true')
    || (typeof import.meta !== 'undefined' && import.meta.env?.VITE_AUDIT_AUTO_DEDUP_ACTIONS === 'true'),
  /**
   * R-75 — auto-rewrite over-confident claims to the evidence band before
   * persistence.
   *
   * DEFAULT OFF (reverted 2026-08-14, same day it was flipped on).
   * Adversarial review proved the rewrite MUTATES PERSISTED REPORT PROSE
   * unsafely: `buildSuggestedRewrite` substituted every band phrase anywhere
   * in the sentence, deleting verbs and source qualifiers —
   *   "Verified utility correspondence indicates a 300 MW allocation."
   *   → "speculative utility correspondence speculative a 300 MW allocation."
   * — and the re-verify then "passed" only because the wreckage no longer
   * parsed as a claim. A HELD report is recoverable; a corrupted
   * investment-grade report shipped to a client is not.
   *
   * The safety bugs are fixed (targeted substitution + safe-substitution
   * guard + exact-offset anchoring), but body-mutating autocorrect stays
   * OPT-IN until a meaning-preserving phrase→phrase downgrade is designed
   * and pinned. The durable fix for systematic over-claiming is
   * PROMPT-level calibration (make the generator write the evidence band),
   * not post-hoc surgery on finished prose.
   */
  AUTOCORRECT_CAVEATS:
    (typeof process !== 'undefined' && process.env?.AUDIT_AUTOCORRECT_CAVEATS === 'true')
    || (typeof import.meta !== 'undefined' && import.meta.env?.VITE_AUDIT_AUTOCORRECT_CAVEATS === 'true'),
  /** R-69 — intra-report cosine threshold for clustering duplicate actions. */
  ACTION_DEDUP_THRESHOLD: parseEnvFloat('AUDIT_ACTION_DEDUP_THRESHOLD', 'VITE_AUDIT_ACTION_DEDUP_THRESHOLD', 0.85),
  /** R-69 — cross-report (executive summary) clustering threshold. */
  ACTION_DEDUP_CROSS_REPORT_THRESHOLD: parseEnvFloat(
    'AUDIT_ACTION_DEDUP_CROSS_REPORT_THRESHOLD',
    'VITE_AUDIT_ACTION_DEDUP_CROSS_REPORT_THRESHOLD',
    0.8,
  ),
  /** R-69 — embedding cache TTL for action text hashes (ms). */
  ACTION_EMBED_CACHE_TTL_MS: parseEnvInt(
    'AUDIT_ACTION_EMBED_CACHE_TTL_MS',
    'VITE_AUDIT_ACTION_EMBED_CACHE_TTL_MS',
    24 * 60 * 60 * 1000,
  ),
  /** R-72 — minimum corpus sample per report type before percentile checks apply. */
  DISTRIBUTION_MIN_SAMPLE: parseEnvInt(
    'AUDIT_DISTRIBUTION_MIN_SAMPLE',
    'VITE_AUDIT_DISTRIBUTION_MIN_SAMPLE',
    5,
  ),
  /** R-131-ROADMAP-FIX-B — LLM pairwise dependency inference for roadmap graphs. Default off. */
  ENABLE_LLM_DEP_INFERENCE:
    (typeof process !== 'undefined' && process.env?.AUDIT_ENABLE_LLM_DEP_INFERENCE === 'true')
    || (typeof import.meta !== 'undefined' && import.meta.env?.VITE_AUDIT_ENABLE_LLM_DEP_INFERENCE === 'true'),
  /** R-131-ROADMAP-FIX-B — max nodes eligible for LLM dep inference; larger graphs skip LLM. */
  GRAPH_LLM_MAX_NODES: parseEnvInt(
    'AUDIT_GRAPH_LLM_MAX_NODES',
    'VITE_AUDIT_GRAPH_LLM_MAX_NODES',
    20,
  ),
  /** R-80 — max LLM dependency-inference pairs per graph build. */
  GRAPH_LLM_MAX_PAIRS: parseEnvInt(
    'AUDIT_GRAPH_LLM_MAX_PAIRS',
    'VITE_AUDIT_GRAPH_LLM_MAX_PAIRS',
    100,
  ),
  /** R-80 — warn when longest dependency chain exceeds this length. */
  CRITICAL_PATH_THRESHOLD: parseEnvInt(
    'AUDIT_CRITICAL_PATH_THRESHOLD',
    'VITE_AUDIT_CRITICAL_PATH_THRESHOLD',
    8,
  ),
  /** R-80 — phase bucket upper bounds in days from reference date. */
  PHASE_BUCKET_DAYS: {
    immediate: 7,
    '30d': 30,
    '90d': 90,
    '6mo': 183,
  } as const,
  /** R-80 — cross-report recommendation clustering threshold. */
  RECOMMENDATION_CLUSTER_THRESHOLD: parseEnvFloat(
    'AUDIT_RECOMMENDATION_CLUSTER_THRESHOLD',
    'VITE_AUDIT_RECOMMENDATION_CLUSTER_THRESHOLD',
    0.8,
  ),
  /** R-80 — max persisted graph build snapshots per site. */
  GRAPH_SNAPSHOT_CAP: 5,
  /** R-77 — strip off-whitelist provider evidence from per-section prompt pools. Default off in prod. */
  STRIP_EVIDENCE_PER_SECTION:
    (typeof process !== 'undefined' && process.env?.AUDIT_STRIP_EVIDENCE_PER_SECTION === 'true')
    || (typeof import.meta !== 'undefined' && import.meta.env?.VITE_AUDIT_STRIP_EVIDENCE_PER_SECTION === 'true'),
  /** J3 — post-gen pipeline must invoke these detectors (short batch IDs). */
  DETECTORS_REQUIRED: ['G1', 'G2', 'H2', 'H3', 'I3', 'J1', 'J2'] as const,
  /** J-batch — per-report integration harness budget (ms). */
  MAX_RUN_MS_PER_REPORT: parseEnvInt(
    'AUDIT_MAX_RUN_MS_PER_REPORT',
    'VITE_AUDIT_MAX_RUN_MS_PER_REPORT',
    10_000,
  ),
  /** J-batch — full corpus integration budget (ms). CI may raise via env. */
  CORPUS_MAX_MS: parseEnvInt(
    'AUDIT_CORPUS_MAX_MS',
    'VITE_AUDIT_CORPUS_MAX_MS',
    typeof process !== 'undefined' && process.env?.CI === 'true' ? 180_000 : 120_000,
  ),
  /** K3 — persist full LLM prompts/responses in trace logs. Default off (PII + cost). */
  STORE_FULL_PROMPTS:
    (typeof process !== 'undefined' && process.env?.AUDIT_STORE_FULL_PROMPTS === 'true')
    || (typeof import.meta !== 'undefined' && import.meta.env?.VITE_AUDIT_STORE_FULL_PROMPTS === 'true'),
  /** K3 — mask email/phone/digit patterns in replay bundles. Default on. */
  MASK_REPLAY_PII: !(
    (typeof process !== 'undefined' && process.env?.AUDIT_MASK_REPLAY_PII === 'false')
    || (typeof import.meta !== 'undefined' && import.meta.env?.VITE_AUDIT_MASK_REPLAY_PII === 'false')
  ),
  /** K3 — warn and offer CSV-only when session exceeds this event count for HTML export. */
  SESSION_REPLAY_MAX_HTML_EVENTS: parseEnvInt(
    'AUDIT_SESSION_REPLAY_MAX_HTML_EVENTS',
    'VITE_AUDIT_SESSION_REPLAY_MAX_HTML_EVENTS',
    1000,
  ),
  /** K3 — suggested retention for LLM traces (days). Enforcement is ops/TTL policy. */
  LLM_TRACE_RETENTION_DAYS: parseEnvInt(
    'AUDIT_LLM_TRACE_RETENTION_DAYS',
    'VITE_AUDIT_LLM_TRACE_RETENTION_DAYS',
    30,
  ),
  /** R-3 — when true, block publish when citation density is below threshold. Default on; set env to false to disable. */
  CITATION_DENSITY_GATE_ENABLED: !(
    (typeof process !== 'undefined' && process.env?.AUDIT_CITATION_DENSITY_GATE === 'false')
    || (typeof import.meta !== 'undefined' && import.meta.env?.VITE_CITATION_DENSITY_GATE === 'false')
  ),
  /** R-3 — default cited-claim fraction required (70%). Lowered from 80%
   * 2026-06-27 (William): the 80% floor BLOCKED content-heavy report types
   * (feasibility / environmental / power-availability / water / interconnection /
   * supply-chain) that legitimately score 73-79% — more claims ⇒ harder to cite
   * every one ⇒ the gate failed the gen (regen fired, couldn't reach 80%, timed
   * out / blocked). 70% keeps a meaningful citation bar while letting these
   * publish. The PROPER follow-up is to raise the GENERATOR's real citation
   * density back toward 80%+ (better grounding/MUST-CITE in the narrative
   * prompt), not to keep the floor low — tracked in memory [[report-gen-citation-density-improvement]]. */
  CITATION_DENSITY_DEFAULT_THRESHOLD: parseEnvFloat(
    'AUDIT_CITATION_DENSITY_DEFAULT_THRESHOLD',
    'VITE_CITATION_DENSITY_DEFAULT_THRESHOLD',
    0.7,
  ),
  /** R-3 — minimum admin-configurable floor (50%). */
  CITATION_DENSITY_MIN_THRESHOLD: parseEnvFloat(
    'AUDIT_CITATION_DENSITY_MIN_THRESHOLD',
    'VITE_CITATION_DENSITY_MIN_THRESHOLD',
    0.5,
  ),
  /** R-3 — max auto MUST CITE focus injections per review loop. */
  CITATION_DENSITY_REGENERATE_MAX: parseEnvInt(
    'AUDIT_CITATION_DENSITY_REGENERATE_MAX',
    'VITE_CITATION_DENSITY_REGENERATE_MAX',
    1,
  ),
  /** R-3 — admin override rationale minimum length. */
  CITATION_DENSITY_OVERRIDE_MIN_RATIONALE: parseEnvInt(
    'AUDIT_CITATION_DENSITY_OVERRIDE_MIN_RATIONALE',
    'VITE_CITATION_DENSITY_OVERRIDE_MIN_RATIONALE',
    40,
  ),
  /** R-6 — subtract cross-field rule penalties from review-loop score. Default off. */
  ENABLE_CROSS_FIELD_SCORING:
    (typeof process !== 'undefined' && process.env?.AUDIT_ENABLE_CROSS_FIELD_SCORING === 'true')
    || (typeof import.meta !== 'undefined' && import.meta.env?.VITE_AUDIT_ENABLE_CROSS_FIELD_SCORING === 'true'),
  /** R-8 — qualitative claim Flash audit (user-audit / batch only). Default off. */
  ENABLE_QUALITATIVE_CLAIM_AUDIT:
    (typeof process !== 'undefined' && process.env?.AUDIT_ENABLE_QUALITATIVE_CLAIM_AUDIT === 'true')
    || (typeof import.meta !== 'undefined' && import.meta.env?.VITE_AUDIT_ENABLE_QUALITATIVE_CLAIM_AUDIT === 'true'),
  /** R-8 — max qualitative claims judged per report audit run. */
  QUALITATIVE_CLAIM_AUDIT_MAX: parseEnvInt(
    'AUDIT_QUALITATIVE_CLAIM_AUDIT_MAX',
    'VITE_AUDIT_QUALITATIVE_CLAIM_AUDIT_MAX',
    50,
  ),
  /** R-9 — cross-section coherence Gemini Flash audit (user-audit-only). Default off. */
  ENABLE_SECTION_COHERENCE_AUDIT:
    (typeof process !== 'undefined' && process.env?.AUDIT_ENABLE_SECTION_COHERENCE === 'true')
    || (typeof import.meta !== 'undefined' && import.meta.env?.VITE_AUDIT_ENABLE_SECTION_COHERENCE === 'true'),
  /** R-9 — max section pairs judged per report (cap 8). */
  SECTION_COHERENCE_MAX_PAIRS: parseEnvInt(
    'AUDIT_SECTION_COHERENCE_MAX_PAIRS',
    'VITE_AUDIT_SECTION_COHERENCE_MAX_PAIRS',
    8,
  ),
  /** R-9 — suggest harmonized wording for warn-level coherence findings (max 1/report). Default off. */
  AUTOCORRECT_COHERENCE:
    (typeof process !== 'undefined' && process.env?.AUDIT_AUTOCORRECT_COHERENCE === 'true')
    || (typeof import.meta !== 'undefined' && import.meta.env?.VITE_AUDIT_AUTOCORRECT_COHERENCE === 'true'),
  /** R-15 — semantic citation-supports-claim verifier (user audit only). Default off. */
  ENABLE_CITATION_SUPPORT_VERIFICATION:
    (typeof process !== 'undefined' && process.env?.AUDIT_ENABLE_CITATION_SUPPORT_VERIFICATION === 'true')
    || (typeof import.meta !== 'undefined' && import.meta.env?.VITE_AUDIT_ENABLE_CITATION_SUPPORT_VERIFICATION === 'true'),
  /** R-15 — max Flash judge calls per verification run. */
  CITATION_SUPPORT_MAX_LLM_CALLS: parseEnvInt(
    'AUDIT_CITATION_SUPPORT_MAX_LLM_CALLS',
    'VITE_AUDIT_CITATION_SUPPORT_MAX_LLM_CALLS',
    30,
  ),
} as const;

function parseEnvFloat(nodeKey: string, viteKey: string, fallback: number): number {
  const raw = (typeof process !== 'undefined' && process.env?.[nodeKey])
    || (typeof import.meta !== 'undefined' && (import.meta.env as Record<string, string | undefined>)?.[viteKey]);
  if (raw == null || raw === '') return fallback;
  const n = parseFloat(String(raw));
  return Number.isFinite(n) ? n : fallback;
}

function parseEnvInt(nodeKey: string, viteKey: string, fallback: number): number {
  const raw = (typeof process !== 'undefined' && process.env?.[nodeKey])
    || (typeof import.meta !== 'undefined' && (import.meta.env as Record<string, string | undefined>)?.[viteKey]);
  if (raw == null || raw === '') return fallback;
  const n = parseInt(String(raw), 10);
  return Number.isFinite(n) ? n : fallback;
}
